/**
 * Actions excluded from redux-state-sync's cross-tab broadcast.
 *
 * Kept in its own module (no store side effects) so the membership can be
 * asserted directly in tests — the ZD-3226 / ZD-3209 fix lives in the PRESENCE
 * of the case/guardian-ID entries here, not just in the action-type names. If a
 * line below is removed the bug silently returns, so crossTabBlacklist.test.ts
 * pins each required entry.
 */
export const crossTabBlacklist = [
  "router",
  "user", // handled by userPersistConfig
  "persist/PERSIST",
  "persist/REHYDRATE",
  /**
   * The idea of this black list is to prevent writing operations
   * to be triggered twice if state is synced through multiple tabs.
   */
  // Meessage actions
  "CREATE_MESSAGE",
  // Case actions
  "CREATE_CASE",
  "PAY_ORDER",
  // A payment message belongs to the tab where the patient started the attempt.
  "cases/paymentAttemptRecorded",
  "cases/dismissPaymentError",
  // The card payment review belongs to the tab where the patient opened it.
  "PREPARE_CARD_PAYMENT",
  "cases/cardPaymentReviewOpened",
  "cases/cardPaymentReviewClosed",
  "UPDATE_CASE",
  "FINALIZE_CASE",
  "ADD_CONDITIONS",
  "SEND_CASE",
  // The Ship screen replaces an Order: another tab running it would mint a duplicate (SHIPUI-R16).
  "FIND_SHIP_CASE",
  "UPDATE_SHIP_METHOD",
  // The Redo screen cancels an Order: another tab running it would send a second cancellation (REDOUI-R24).
  "FIND_REDO_ORDER",
  "LOAD_REDO_PRESCRIPTIONS",
  "LOAD_REDO_INVOICE",
  "CANCEL_REDO_ORDER",
  "REPLACE_REDO_ORDER",
  "RESEND_REDO_ORDER",
  // The User Search creates an address: another tab running it would create it twice (USER-R25).
  "SAVE_USER_ADDRESS",
  "PAY_CASE",
  "newCase/updateConditionMedication",
  /**
   * The `case` slice is sessionStorage-backed (per-tab). Any action that writes
   * or resets newCase.data.id must not broadcast, or one tab clobbers another
   * tab's in-progress case — orphaning the guardian-ID ownership marker and
   * dropping the "Photo uploaded successfully" banner (ZD-3226 / ZD-3209).
   *   - newCaseSuccess: a second tab's case creation would overwrite tab 1's id.
   *   - clearNewCase: dispatched on logout AND on routine dashboard/ThankYou
   *     navigation, so a second tab would reset tab 1's in-progress case id.
   * Note: condition/question mutations (setConditions/updateCaseQuestions/…) are
   * intentionally left synced; only id-writing actions are isolated here.
   */
  "newCase/newCaseSuccess",
  "newCase/clearNewCase",
  // User actions
  "SIGNUP",
  "PASSWORD_UPDATE",
  "UPDATE_SHIPPING_ADDRESS",
  "SET_DEFAULT_PAYMENT_METHOD",
  "DELETE_PAYMENT_METHODS",
  "UPDATE_PAYMENT_METHOD",
  "ADD_PAYMENT_METHOD",
  "USER_UPDATE",
  /**
   * SUBMIT_PHOTO_ID carries a non-serializable File in its payload — without
   * blacklisting it, redux-state-sync would try to rebroadcast it to every
   * open tab, which fails to serialize and would also re-trigger the
   * three-call upload sequence a second time per open tab.
   */
  "SUBMIT_PHOTO_ID",
  /**
   * The user/userUpdate slice action carries tab-local form edits (email,
   * uploaded guardian ID, etc.). Broadcasting it lets one tab overwrite
   * another's in-progress fields — e.g. a fresh second-tab boot erasing a
   * guardian ID uploaded in the first tab (ZD-3226 / ZD-3209).
   */
  "user/userUpdate",
  /**
   * setGuardianId carries the tab-local guardian-ID upload plus its
   * owning-case marker. Like user/userUpdate it must not broadcast, or a
   * second tab's upload/boot could clobber the first tab's in-progress ID
   * (ZD-3226).
   */
  "user/setGuardianId",
  "LOGOUT",
];
